import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { AppState } from '../../store';
import DraggableComponent from '../DraggableComponent';
import type { IDefaultAssignmentsDialogProps } from '../../../types';
import { VariableAssignmentsTable } from '../VariableAssignmentsTable';

/**
 * Dialog component to display default assignments on load or before save
 * @description Shows all configuration variables that are assigned by default values.
 * Rendered on initial configuration load (if defaults exist) and when the user clicks Save/Add Product.
 * - Cancel: closes dialog without saving
 * - Ok: closes dialog and triggers the save/redirect flow
 * @param {IDefaultAssignmentsDialogProps} props - Component props
 * @returns {JSX.Element | null} Default Assignment Dialog component
 */
export const DefaultAssignmentsDialog: React.FC<IDefaultAssignmentsDialogProps> = ({
  assignmentsByDefault,
  count,
  onOk,
  onCancel,
}) => {
  const { t } = useTranslation();
  const applicationSettings = useSelector((state: AppState) => state.applicationSettings);

  if (count === 0 || !assignmentsByDefault) {
    return null;
  }

  const classSelector = Number(applicationSettings?.pageSize?.isExtraSmall);
  const flexDirection = ['flex-row', 'flex-column'];
  const alignSelf = ['', 'align-start'];

  return (
    <Dialog
      className="AutoSelectedAssignmentDialog-root"
      open={true}
      maxWidth="md"
      fullWidth
      slotProps={{ paper: { className: 'AutoSelectedAssignmentDialog-paper' } }}
      PaperComponent={DraggableComponent}
      aria-labelledby="draggable-dialog-title"
    >
      <DialogTitle className="header d-flex align-center cursor-move h-auto">
        {t('defaultAssignment.defaultSelectionsFound')}
      </DialogTitle>
      <DialogContent className="dialogContent">
        <Box className={`selection ${flexDirection[classSelector]} ${alignSelf[classSelector]}`}>
          <div className="auto-included-message">{t('defaultAssignment.message')}</div>
        </Box>
        <Accordion
          defaultExpanded
          disableGutters
          sx={{
            '&.MuiAccordion-root': {
              margin: '8px 0',
            },
            '&.MuiAccordion-root:before': {
              display: 'none',
            },
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            {t('defaultAssignment.pendingSelections')} ({count}):
          </AccordionSummary>
          <AccordionDetails
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              minHeight: 0,
              padding: '8px 16px 16px',
            }}
          >
            <Divider />
            <div style={{ marginTop: '10px', flexShrink: 0 }} className="pending-section-info">
              {t('defaultAssignment.pendingSectionInfo', { count })}
            </div>
            <div
              className="scrollableContent"
              style={{ maxHeight: '35vh', overflowY: 'auto', overflowX: 'hidden' }}
            >
              {Object.entries(assignmentsByDefault).map(([productKey, sections]) => (
                <VariableAssignmentsTable
                  key={productKey}
                  productKey={productKey}
                  sections={sections}
                />
              ))}
            </div>
          </AccordionDetails>
        </Accordion>
      </DialogContent>
      <DialogActions className="footer">
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            width: '100%',
            gap: '8px',
          }}
        >
          <Button className="text-capitalize" color="info" variant="contained" onClick={onOk}>
            {t('button.saveWithDefaults')}
          </Button>
          <Button className="text-capitalize" color="primary" variant="outlined" onClick={onCancel}>
            {t('button.cancel')}
          </Button>
        </div>
      </DialogActions>
    </Dialog>
  );
};
